/* THE FONT LIBRARY — the table, and the pure functions over it.

   Pure: no DOM, no document.fonts. The runtime registry that actually loads
   faces is scene/fonts.js; this half is separate because core/doc.js needs to
   migrate old documents against the table and core/ must not reach into the
   browser.

   Text in this app is not rendered, it is TRACED — glyphs are rasterised to a
   canvas and marching squares walks the alpha channel. Two consequences drive
   everything here:

   1. A font that has not loaded is a CORRECTNESS bug, not a cosmetic one. Canvas
      silently substitutes a fallback and the wrong letterforms get extruded into
      real geometry, with nothing reported.
   2. A system font is not portable. The geometry comes from whatever face the
      machine happens to have, so a project built on a Mac opens on Windows as a
      different mesh — a different deliverable. That is why Impact, Georgia,
      Helvetica, Courier and system-ui were dropped: every font is now either
      bundled here or uploaded with the project.

   Files are self-hosted through @fontsource — same privacy and offline
   properties as vendoring by hand, but version-pinned in package.json and
   shipped with their licences. Every family is SIL Open Font Licence, so
   bundling and commercial use are permitted. */

export const FALLBACK_FAMILY = 'Chivo';

/* Weighted toward heavy and condensed on purpose: anamorphic work needs
   letterforms with presence, and thin faces trace into fragile geometry that
   disappears once it has depth.

   `pkg` is the @fontsource package. src/styles/fonts.css is GENERATED from this
   table by tools/gen-fonts-css.mjs, and tools/verify.mjs checks the two agree —
   a weight declared here but never imported there would 404, which shows up as
   silently wrong geometry rather than an error.

   No 300 weight anywhere. It traces worst, and offering Chivo Light while only
   400/700/900 were ever loaded was a real bug in the previous build. */
export const FONTS = [
  { family: 'Anton',            pkg: 'anton',            category: 'Display & condensed', weights: [400] },
  { family: 'Bebas Neue',       pkg: 'bebas-neue',       category: 'Display & condensed', weights: [400] },
  { family: 'Archivo Black',    pkg: 'archivo-black',    category: 'Display & condensed', weights: [400] },
  { family: 'Bungee',           pkg: 'bungee',           category: 'Display & condensed', weights: [400] },
  { family: 'Oswald',           pkg: 'oswald',           category: 'Display & condensed', weights: [400, 700] },

  { family: 'Inter',            pkg: 'inter',            category: 'Sans',                weights: [400, 700, 900] },
  { family: 'Chivo',            pkg: 'chivo',            category: 'Sans',                weights: [400, 700, 900] },
  { family: 'Montserrat',       pkg: 'montserrat',       category: 'Sans',                weights: [400, 700, 900] },
  { family: 'Poppins',          pkg: 'poppins',          category: 'Sans',                weights: [400, 700, 900] },
  { family: 'Rubik',            pkg: 'rubik',            category: 'Sans',                weights: [400, 700, 900] },

  { family: 'Playfair Display', pkg: 'playfair-display', category: 'Serif',               weights: [400, 700, 900] },
  { family: 'Lora',             pkg: 'lora',             category: 'Serif',               weights: [400, 700] },
  { family: 'Zilla Slab',       pkg: 'zilla-slab',       category: 'Serif',               weights: [400, 700] },

  { family: 'Space Grotesk',    pkg: 'space-grotesk',    category: 'Technical',           weights: [400, 700] },
  { family: 'Space Mono',       pkg: 'space-mono',       category: 'Technical',           weights: [400, 700] },

  { family: 'Lobster',          pkg: 'lobster',          category: 'Script',              weights: [400] }
];

export const CATEGORIES = ['Display & condensed', 'Sans', 'Serif', 'Technical', 'Script'];

const byFamily = new Map(FONTS.map(f => [f.family, f]));

/** The library entry for a family, or null if it is not bundled. */
export const builtIn = family => byFamily.get(family) || null;

/** Weights a bundled family actually ships. Uploaded families are not known here. */
export const builtInWeights = family => (byFamily.get(family) || {}).weights || null;

/**
 * Snap to a weight that exists in `weights`.
 *
 * Asking canvas for a weight a face does not ship gets a SYNTHESISED one —
 * strokes thickened by the rasteriser rather than drawn that way by the
 * designer — and that synthesis is what gets traced into geometry.
 */
export function snapWeight(weights, weight) {
  if (!weights || !weights.length) return +weight || 400;
  const w = +weight || 400;
  return weights.reduce((best, cur) => Math.abs(cur - w) < Math.abs(best - w) ? cur : best, weights[0]);
}

/* ── the font string ─────────────────────────────────────────────────────────
   ⚠️ A family name containing a space MUST be quoted. `400 64px Playfair Display`
   is not a valid CSS font shorthand, and assigning an invalid string to ctx.font
   is a SILENT no-op — the canvas keeps whatever face it had and you trace the
   previous family. Every font string in the app is built here so that cannot be
   right in one place and wrong in another. */

export const quoteFamily = family =>
  /^[A-Za-z][\w-]*$/.test(family) ? family : JSON.stringify(String(family || ''));

/** For ctx.font. Carries a fallback so something renders while the face loads. */
export const fontSpec = (family, weight, px) =>
  `${weight} ${px}px ${quoteFamily(family)}, sans-serif`;

/* For document.fonts.check / load — deliberately NO fallback.
   check('16px Anton, sans-serif') is true the instant sans-serif exists, which is
   always, so a fallback here would report every font as ready and defeat the
   entire point of asking. */
export const matchSpec = (family, weight) => `${weight} 16px ${quoteFamily(family)}`;

/* ── migrating away from system fonts ────────────────────────────────────────
   Projects saved before the library existed name a CSS stack. Map each to the
   closest bundled face so an old file opens with type of similar weight and
   proportion, rather than silently falling back to Chivo. */
export const LEGACY_FONTS = {
  'Impact, Haettenschweiler, sans-serif': 'Anton',        // the standard free Impact substitute
  'Helvetica, Arial, sans-serif':         'Inter',        // neo-grotesque, closest in feel
  'system-ui, sans-serif':                'Inter',        // Inter *is* the archetypal UI sans
  'Georgia, serif':                       'Lora',         // transitional serif, similar warmth
  '"Courier New", monospace':             'Space Mono',
  'Chivo, Helvetica, Arial':              'Chivo'         // already bundled
};

/**
 * Resolve whatever a document names into a family this app can use.
 *
 * @param {string} font                the stored value: a bare family or a legacy CSS stack
 * @param {string[]} [docFamilies]     families the document brings with it (uploads)
 */
export function resolveFamily(font, docFamilies) {
  const f = String(font || '').trim();
  if (!f) return FALLBACK_FAMILY;
  if (byFamily.has(f)) return f;
  if (docFamilies && docFamilies.includes(f)) return f;
  if (LEGACY_FONTS[f]) return LEGACY_FONTS[f];
  /* An unrecognised stack: take its first family and see if that is bundled, so
     'Montserrat, sans-serif' still finds Montserrat. */
  const first = f.split(',')[0].trim().replace(/^["']|["']$/g, '');
  if (byFamily.has(first)) return first;
  if (docFamilies && docFamilies.includes(first)) return first;
  return FALLBACK_FAMILY;
}
